import { useEffect, useRef, useState, useCallback } from 'react';

export interface WebSocketMessage {
  action: string;
  [key: string]: any;
}

interface UseWebSocketProps {
  url: string;
  onMessage?: (data: WebSocketMessage) => void;
}

export function useWebSocket({ url, onMessage }: UseWebSocketProps) {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const messageQueue = useRef<WebSocketMessage[]>([]);
  const reconnectRef = useRef(true);
  const isInitialMount = useRef(true);

  const connect = useCallback(() => {
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setIsConnected(true);
      // while (messageQueue.current.length > 0) {
      //   const queuedMsg = messageQueue.current.shift();
      //   if (queuedMsg) {
      //     ws.send(JSON.stringify(queuedMsg));
      //   }
      // }
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as WebSocketMessage;
        
        if (!data.action && !data.type) {
          return;
        }
        
        onMessage && onMessage(data);
      } catch (err) {
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = (event) => {
      console.log('WebSocket closed', event.code, event.reason);
      setIsConnected(false);
      wsRef.current = null;

      if (reconnectRef.current && (event.code === 1001 || event.code === 1006)) {
        setTimeout(() => {
          connect();
        }, 2000);
      }
    };
  }, [url, onMessage]);

  useEffect(() => {
    reconnectRef.current = true;
    const initDelay = isInitialMount.current ? 500 : 0;
    isInitialMount.current = false;
    
    const timer = setTimeout(() => {
      connect();
    }, initDelay);

    return () => {
      clearTimeout(timer);
      reconnectRef.current = false;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    };
  }, [connect]);

  const sendMessageInternal = useCallback((msg: WebSocketMessage, queue: boolean = true) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else if (queue) {
      messageQueue.current.push(msg);
    }
  }, []);

  const sendMessage = useCallback((msg: WebSocketMessage) => {
    sendMessageInternal(msg, true);
  }, [sendMessageInternal]);

  const subscribeMessage = useCallback((msg: WebSocketMessage) => {
    sendMessageInternal(msg, true);
  }, [sendMessageInternal]);

  return { isConnected, sendMessage, subscribeMessage };
}